/* eslint-disable no-redeclare */
/* eslint-disable no-unused-vars */

import React, { useState } from 'react';
import ReactModal from 'react-modal';
import { RegularButton, AlertAdapter, CommonDropdown, ICommonDropdownOption, FullScreenLoading } from 'pg-ui-library';
import { CloseIcon } from 'src/modules/login/components/mfa-modal/styles';
import closeSvg from 'src/assets/close.svg';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { AxiosResponse } from 'axios';
import UserService from 'src/services/user.service';
import { useQuery } from '@tanstack/react-query';
import {
  UserAdditionBody,
  CommonInputDiv,
  UserAdditionForm,
  UserAdditionHeader,
  UserAdditionFormRow,
  UserAdditionFormRowLast,
  InputContainer,
} from './styles';
import { User } from '../add-adminusers-modal';
import { usePromoteuserSupport } from '../../mutations/user-setting.mutation';

interface AddUsersProps {
  isOpen: boolean;
  onRequestClose: () => void;
}

export enum Application {
  ESS = 'ESS',
}

const ApplicationOptions: ICommonDropdownOption[] = [
  { key: '0', label: 'Select', value: '' },
  { key: '2', label: Application.ESS, value: Application.ESS },
];

const AddSupportAdminUsers: React.FC<AddUsersProps> = ({ isOpen, onRequestClose }) => {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedUserEmail, setSelectedUserEmail] = useState<string>('');
  const [selectedUserApp, setSelectedUserApp] = useState<string>(Application.ESS);
  const promoteuserSupport = usePromoteuserSupport();

  const validationSchema = yup.object().shape({
    application: yup.string().required('Application is required'),
  });

  const formik = useFormik({
    initialValues: {
      userId: selectedUserId,
      application: selectedUserApp,
      email: selectedUserEmail,
    },
    validationSchema,
    onSubmit: async values => {
      try {
        await promoteuserSupport.mutateAsync({ promoteUserSupport: values });
        AlertAdapter('User has been added to the support!', 'success');
        onRequestClose();
      } catch (error: any) {
        const errorMsg = error?.toString() || 'An error occurred while adding the support';
        AlertAdapter(errorMsg, 'error');
      }
    },
  });

  const usersSearch = useQuery<AxiosResponse<{ items: User[] }>>(['/admin/users/search'], async () => {
    try {
      const result = await UserService.getUsersSearchNew('');
      return result;
    } catch (e) {
      const errorMessage = `Error fetching users: ${e}`;
      AlertAdapter(errorMessage, 'error');
      throw new Error(errorMessage);
    }
  });

  function setStatusTicket(value: string) {
    setSelectedUserApp(value);
  }

  const renderDropdownOptions = () => {
    if (usersSearch.isLoading) {
      return <FullScreenLoading />;
    }
    if (usersSearch.error) {
      return <p>Error fetching users</p>;
    }
    const options =
      usersSearch.data?.data.items.map(user => ({
        key: user.id,
        label: user.email,
        value: user.email,
      })) || [];

    return (
      <CommonDropdown
        id="Email"
        name="Email"
        label="Email"
        options={[{ key: '', label: 'Select Email', value: '' }, ...options]}
        value={formik.values.email}
        setValue={(value: string) => {
          const selectedUser = usersSearch.data?.data.items.find(user => user.email === value);
          if (selectedUser) {
            setSelectedUserEmail(value);
            setSelectedUserId(selectedUser.id);
            formik.setFieldValue('email', value);
            formik.setFieldValue('userId', selectedUser.id);
          }
        }}
      />
    );
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '30px',
          margin: 'auto',
          width: '60%',
          height: '310px',
          display: 'flex',
          flexDirection: 'column',
          padding: '0 20px 0 20px',
          background: '#F3F4F8',
          overflow: 'hidden',
        },
      }}
    >
      <UserAdditionHeader>
        Add Support Admin User
        <CloseIcon src={closeSvg} onClick={onRequestClose} />
      </UserAdditionHeader>
      <UserAdditionForm onSubmit={formik.handleSubmit}>
        <UserAdditionBody>
          <InputContainer>
            <UserAdditionFormRow>
              <CommonInputDiv>{renderDropdownOptions()}</CommonInputDiv>

              <CommonInputDiv>
                <CommonDropdown
                  name="Application"
                  label="Application"
                  style={{ minWidth: '211px' }}
                  options={ApplicationOptions}
                  setValue={(value: string) => {
                    formik.setFieldValue('application', value);
                    setStatusTicket(value);
                  }}
                />
              </CommonInputDiv>
            </UserAdditionFormRow>

            <UserAdditionFormRowLast>
              <RegularButton
                type="submit"
                onClick={() => formik.handleSubmit()}
                style={{ marginTop: '35px', marginLeft: '87%' }}
                disabled={formik.isSubmitting || selectedUserEmail === ''}
              >
                Save
              </RegularButton>
            </UserAdditionFormRowLast>
          </InputContainer>
        </UserAdditionBody>
      </UserAdditionForm>
    </ReactModal>
  );
};

export default AddSupportAdminUsers;
